import {
  FormLabel,
  NumberInputField,
  NumberInput,
  Select,
  useToast,
} from "@chakra-ui/react";
import { TransactionType, OrderTypes } from "../views/Orders/enums";
import { useState, useEffect, useMemo } from "react";
import { isInvalidDiscountAndCredit } from "../utils/util";
import GaraazAlertDialogue from "./GaraazAlertDialogue";
import services from "../views/Orders/services";
import { useGetOrderMediaConfig } from "../views/PlaceOrderTest/queryHooks";
import { useUserDetailsContext } from "../context/userDetailsContext";
import { useGetAllSuppliers } from "../views/Orders/queryHooks";
import {
  checkCustomerHasMgpAssociation,
  DEFAULT_MGP_DISTRIBUTOR_ID,
  resolveDefaultEstimateSplitTransactionType,
} from "./DeliveryOptions/utils/resolveDefaultEstimateSplitTransactionType";

interface TransactionTypeSelectComponentProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (
    transactionType: string,
    discount?: number,
    creditNotePercentage?: number,
  ) => void | Promise<void>;
  onFail?: () => void;
  supplierName?: string;
  supplierId?: string;
  garaazSupplierIdFromConfig?: string;
  orderId: string;
  setTransactionType: (transactionType: string) => void;
  orderData?: any;
  isLoading?: boolean;
}

const TransactionTypeSelectComponent = ({
  isOpen,
  onClose,
  onSuccess,
  onFail,
  supplierName,
  supplierId,
  garaazSupplierIdFromConfig,
  orderId,
  setTransactionType,
  orderData,
  isLoading = false,
}: TransactionTypeSelectComponentProps) => {
  const [selectedTransactionType, setSelectedTransactionType] =
    useState<string>("");
  const [discount, setDiscount] = useState<string>("");
  const [creditNotePercentage, setCreditNotePercentage] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const toast = useToast();
  const { data: mobileConfigs, isLoading: isConfigLoading } = useGetOrderMediaConfig();

  const isRegularOrVirtualOrder =
    orderData?.orderType === OrderTypes.REGULAR ||
    orderData?.orderType === OrderTypes.VIRTUAL;

  const isSupplierGaraaz = Boolean(
    isRegularOrVirtualOrder &&
    supplierId &&
    supplierId === (garaazSupplierIdFromConfig || "64ae87c051c0be36170dc718"),
  );

  const isSingleInvoiceRequired = Boolean(
    orderData?.billingDetails?.isSingleGaraazInvoiceRequired,
  );

  const { data: allSuppliers, isLoading: isSuppliersLoading } = useGetAllSuppliers();
  const isDataLoading = isConfigLoading || isSuppliersLoading;

  const mgpDistributorConfig =
    (mobileConfigs as any)?.mgp_distributor || DEFAULT_MGP_DISTRIBUTOR_ID;

  const resolvedTransactionType = useMemo(() => {
    const defaultType = resolveDefaultEstimateSplitTransactionType({
      supplierId,
      orderData,
      isSingleGaraazInvoiceRequired: isSingleInvoiceRequired,
      garaazSupplierIdFromConfig,
      mgpDistributorConfig,
      allSuppliers,
    });
    return defaultType || TransactionType.TRADING;
  }, [
    supplierId,
    orderData,
    isSingleInvoiceRequired,
    garaazSupplierIdFromConfig,
    mgpDistributorConfig,
    allSuppliers,
    isSupplierGaraaz,
  ]);

  const userDetails = useUserDetailsContext();

  const isUserAllowedToEdit = useMemo(() => {
    const allowedIdsRaw = (mobileConfigs as any)?.override_edit_user_ids || "";
    if (!allowedIdsRaw) return false;
    const allowedIds = String(allowedIdsRaw)
      .split(",")
      .map((id: string) => id.trim().toLowerCase())
      .filter(Boolean);
    return Boolean(
      userDetails?._id &&
        allowedIds.includes(String(userDetails._id).trim().toLowerCase()),
    );
  }, [mobileConfigs, userDetails]);

  const isFacilitationBlocked = useMemo(() => {
    const isHardBlocked = isSupplierGaraaz || isSingleInvoiceRequired;
    if (isUserAllowedToEdit) {
      return isHardBlocked;
    }
    return isHardBlocked || resolvedTransactionType !== TransactionType.FACILITATION;
  }, [isSupplierGaraaz, isSingleInvoiceRequired, isUserAllowedToEdit, resolvedTransactionType]);

  const isTradingBlocked = useMemo(() => {
    if (isUserAllowedToEdit) {
      return false;
    }
    return resolvedTransactionType !== TransactionType.TRADING;
  }, [isUserAllowedToEdit, resolvedTransactionType]);

  useEffect(() => {
    if (isOpen && !isDataLoading) {
      setSelectedTransactionType(resolvedTransactionType);
      setDiscount("");
      setCreditNotePercentage("");
    }
  }, [isOpen, resolvedTransactionType, isDataLoading]);

  const hasMgpAssociation = useMemo(() => {
    return checkCustomerHasMgpAssociation(
      orderData,
      mgpDistributorConfig,
      allSuppliers,
    );
  }, [orderData, mgpDistributorConfig, allSuppliers]);

  const handleTransactionTypeChange = (value: string) => {
    if (isFacilitationBlocked && value === TransactionType.FACILITATION) {
      let description = "Facilitation is not possible when Single Invoice is required.";
      if (isSupplierGaraaz) {
        description = "Facilitation is not possible when allocating to Garaaz.";
      } else if (!hasMgpAssociation) {
        description = "Facilitation is not possible without customer MGP association.";
      }

      toast({
        title: "Transaction Type Restricted",
        description,
        status: "warning",
        duration: 4000,
        isClosable: true,
        position: "top",
      });
      return;
    }
    setSelectedTransactionType(value);
  };

  const handleDiscountAndCreditNotePercentageChange = (
    value: string,
    field: string,
  ) => {
    const numValue = Number(value);
    if (numValue >= 0 && numValue <= 100) {
      if (field === "Discount") {
        setDiscount(value);
      } else if (field === "Customer credit") {
        setCreditNotePercentage(value);
      }
    }
  };

  const handleConfirm = async () => {
    if (!selectedTransactionType) {
      return;
    }

    if (selectedTransactionType === TransactionType.TRADING) {
      const isInvalid = isInvalidDiscountAndCredit(
        +discount,
        +creditNotePercentage,
      );
      if (isInvalid) {
        return;
      }
    }

    setIsSubmitting(true);

    try {
      if (orderId && setTransactionType) {
        const payload: {
          orderDiscount?: string;
          creditNotePercentage?: string;
          transactionType: string;
        } = {
          transactionType: selectedTransactionType,
        };

        if (selectedTransactionType === "Trading") {
          payload.orderDiscount = discount;
          payload.creditNotePercentage = creditNotePercentage;
        }

        await services.updateOrder(orderId, payload);
        setTransactionType(selectedTransactionType);
      }

      await onSuccess(
        selectedTransactionType,
        selectedTransactionType === TransactionType.TRADING
          ? +discount
          : undefined,
        selectedTransactionType === TransactionType.TRADING
          ? +creditNotePercentage
          : undefined,
      );

      toast({
        description: "Transaction type updated successfully.",
        status: "success",
        duration: 4000,
        isClosable: true,
        position: "top",
      });
      onClose();
    } catch (error: any) {
      console.error("Error updating transaction type:", error);
      const description =
        error?.response?.data?.errorMsg?.[0]?.title ??
        error?.message ??
        "Failed to update transaction type.";
      toast({
        description,
        status: "error",
        duration: 4000,
        isClosable: true,
        position: "top",
      });
      onFail?.();
    } finally {
      setIsSubmitting(false);
    }
  };

  const isSubmitDisabled =
    isDataLoading ||
    !selectedTransactionType ||
    (selectedTransactionType === TransactionType.TRADING &&
      isInvalidDiscountAndCredit(+discount, +creditNotePercentage));

  return (
    <GaraazAlertDialogue
      isOpen={isOpen}
      onOpen={() => {}}
      onClose={onClose}
      onSubmitBtn={handleConfirm}
      alertBodyText={
        <>
          <FormLabel
            marginRight={"auto"}
            fontSize={12}
            fontWeight={600}
            marginBottom={0}
          >
            Transaction Type
          </FormLabel>
          <Select
            placeholder={isDataLoading ? "Loading..." : "Transaction Type"}
            value={selectedTransactionType}
            isDisabled={isDataLoading}
            onChange={(e) => handleTransactionTypeChange(e.target.value)}
          >
            <option
              value={`${TransactionType.FACILITATION}`}
              disabled={isFacilitationBlocked}
            >
              {TransactionType.FACILITATION}
            </option>
            <option
              value={`${TransactionType.TRADING}`}
              disabled={isTradingBlocked}
            >
              {TransactionType.TRADING}
            </option>
          </Select>
          {selectedTransactionType === TransactionType.TRADING && (
            <>
              <FormLabel
                marginRight={"auto"}
                fontSize={12}
                fontWeight={600}
                marginTop={4}
                marginBottom={0}
              >
                Discount
              </FormLabel>

              <NumberInput
                width={"100%"}
                value={discount}
                onChange={(value) => {
                  handleDiscountAndCreditNotePercentageChange(
                    value,
                    "Discount",
                  );
                }}
                size={"sm"}
              >
                <NumberInputField placeholder="Enter Customer Discount %" />
              </NumberInput>
              <FormLabel
                marginRight={"auto"}
                fontSize={12}
                fontWeight={600}
                marginTop={4}
                marginBottom={0}
              >
                Credit
              </FormLabel>

              <NumberInput
                width={"100%"}
                value={creditNotePercentage}
                onChange={(value) => {
                  handleDiscountAndCreditNotePercentageChange(
                    value,
                    "Customer credit",
                  );
                }}
                size={"sm"}
              >
                <NumberInputField placeholder="Enter Customer Credit %" />
              </NumberInput>
            </>
          )}
        </>
      }
      alertDialogueHeaderText={`Select Transaction Type for ${
        supplierName || "Supplier"
      }`}
      leftBtnText="Cancel"
      submitBtnText="Confirm"
      isSubmitBtnDisabled={isSubmitDisabled}
      isSubmitBtnLoading={isSubmitting || isLoading}
      isCentered={true}
    />
  );
};

export default TransactionTypeSelectComponent;
